import { Head } from 'vite-react-ssg';
import { Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Brain, Cpu, Zap, Bot, Database, Code2, Chrome, CheckCircle, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const DeepseekAI = () => {
  const faqItems = [
    {
      question: "What is DeepSeek AI?",
      answer: "DeepSeek AI is a Chinese artificial intelligence company that develops open-source large language models. Founded in 2023, DeepSeek has become known for creating powerful AI models like DeepSeek-V3 and DeepSeek R1 that rival competitors at a fraction of the training cost."
    },
    {
      question: "Is DeepSeek AI free to use?",
      answer: "Yes, DeepSeek AI offers generous free access. You can use DeepSeek through their web chat, API with free tier, or download the open-source models to run locally at no cost."
    },
    {
      question: "How does DeepSeek compare to ChatGPT?",
      answer: "DeepSeek matches or exceeds ChatGPT on many benchmarks, especially in coding and mathematical reasoning. The key differences are that DeepSeek is open-source, significantly cheaper, and can run locally for privacy."
    },
    {
      question: "Is DeepSeek AI safe and secure?",
      answer: "DeepSeek includes content filtering and safety measures. As an open-source model, its code is publicly auditable. For maximum security, you can run DeepSeek locally on your own infrastructure."
    },
    {
      question: "What can DeepSeek AI do?",
      answer: "DeepSeek AI excels at natural language understanding, code generation, mathematical reasoning, writing, analysis, and general conversation. It's particularly strong in coding tasks and logical reasoning."
    },
    {
      question: "How much does DeepSeek cost?",
      answer: "DeepSeek offers a generous free tier. API pricing is very competitive at around $0.15 per million input tokens and $0.60 per million output tokens, making it one of the most cost-effective AI models available."
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqItems.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  };

  // This page is ABOUT DeepSeek (the company, at deepseek.com); it is published by
  // this independent site. Never describe DeepSeek as living at deepseek.ai.
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "What Is DeepSeek AI? An Independent Overview",
    "url": "https://deepseek.ai/deepseek-ai",
    "about": {
      "@type": "Organization",
      "name": "DeepSeek",
      "legalName": "Hangzhou DeepSeek Artificial Intelligence Co., Ltd.",
      "description": "Chinese artificial intelligence company developing open-weight large language models.",
      "url": "https://www.deepseek.com",
      "foundingDate": "2023"
    },
    "publisher": {
      "@type": "Organization",
      "name": "The Independent DeepSeek Guide",
      "url": "https://deepseek.ai"
    }
  };

  return (
    <>
      <Head>
        <title>What Is DeepSeek AI? An Independent Overview (2026)</title>
        <meta name="description" content="Independent overview of DeepSeek AI: who makes the open-weight models, where to use the official chat, and how they compare with ChatGPT, Claude and Gemini." />
        <meta name="keywords" content="DeepSeek AI, open-source AI, language models, AI revolution, DeepSeek-V3, DeepSeek R1, free AI, AI coding" />
        <link rel="canonical" href="https://deepseek.ai/deepseek-ai" />
        
        <meta property="og:title" content="What Is DeepSeek AI? An Independent Overview" />
        <meta property="og:description" content="Explore DeepSeek AI's open-source language models. Free, cost-effective AI with advanced capabilities." />
        <meta property="og:url" content="https://deepseek.ai/deepseek-ai" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://deepseek.ai/og-image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="What Is DeepSeek AI? An Independent Overview" />
        <meta name="twitter:description" content="Explore DeepSeek AI's open-source language models. Free, cost-effective AI with advanced capabilities." />
        <meta name="twitter:image" content="https://deepseek.ai/og-image.png" />
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
      </Head>

      <div className="min-h-screen pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <h1 className="text-4xl font-bold sm:text-5xl md:text-6xl">
              <span className="block">What Is DeepSeek AI?</span>
              <span className="block text-primary mt-2">An Independent Overview</span>
            </h1>
            <p className="mt-4 text-xl text-muted-foreground max-w-3xl mx-auto">
              DeepSeek is a Chinese AI company that publishes open-weight language models. This independent guide explains its models and how they compare. It is not affiliated with DeepSeek. The official site is{" "}
              <a href="https://www.deepseek.com" target="_blank" rel="noopener noreferrer" className="underline">deepseek.com</a>.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row justify-center gap-4">
              <a
                href="https://chromewebstore.google.com/detail/ai-sidebar-with-deepseek/inhcgfpbfdjbjogdfjbclgolkmhnooop"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-8 py-4 text-base font-medium rounded-full text-primary-foreground bg-primary hover:bg-primary/90 transition-all shadow-lg"
              >
                <Chrome className="mr-2 h-5 w-5" />
                Add to Chrome - It's Free
              </a>
              <Link
                to="/blog/what-is-deepseek-ai"
                className="inline-flex items-center justify-center px-6 py-3 border-2 border-primary text-base font-medium rounded-full text-primary bg-background hover:bg-accent transition-all"
              >
                Read the full DeepSeek explainer
              </Link>
            </div>
          </div>

          {/* Key Features */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <Brain className="h-8 w-8 text-primary mb-4" />
                <CardTitle>Advanced Language Models</CardTitle>
                <CardDescription>State-of-the-art natural language processing capabilities</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  DeepSeek's models handle context, long documents and complex language tasks, and its API is priced well below most Western competitors.
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <Cpu className="h-8 w-8 text-primary mb-4" />
                <CardTitle>Cost-Efficient Computing</CardTitle>
                <CardDescription>Optimized architectures for maximum performance</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Built with innovative MoE architecture and Multi-Head Latent Attention to deliver state-of-the-art results at dramatically lower computing costs.
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <Zap className="h-8 w-8 text-primary mb-4" />
                <CardTitle>Open-Source Advantage</CardTitle>
                <CardDescription>Transparent, accessible, and collaborative</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  DeepSeek publishes many of its model weights under permissive licenses, so developers can download, run and fine-tune them.
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Comparison Table */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-center mb-8">DeepSeek vs Leading AI Models</h2>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[200px]">Feature</TableHead>
                    <TableHead className="text-center">DeepSeek</TableHead>
                    <TableHead className="text-center">ChatGPT</TableHead>
                    <TableHead className="text-center">Claude</TableHead>
                    <TableHead className="text-center">Gemini</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell className="font-medium">Open Source</TableCell>
                    <TableCell className="text-center"><CheckCircle className="h-5 w-5 text-green-500 mx-auto" /></TableCell>
                    <TableCell className="text-center"><XCircle className="h-5 w-5 text-red-500 mx-auto" /></TableCell>
                    <TableCell className="text-center"><XCircle className="h-5 w-5 text-red-500 mx-auto" /></TableCell>
                    <TableCell className="text-center"><XCircle className="h-5 w-5 text-red-500 mx-auto" /></TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Free Tier</TableCell>
                    <TableCell className="text-center">Generous</TableCell>
                    <TableCell className="text-center">Limited</TableCell>
                    <TableCell className="text-center">Limited</TableCell>
                    <TableCell className="text-center">Generous</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Local Deployment</TableCell>
                    <TableCell className="text-center"><CheckCircle className="h-5 w-5 text-green-500 mx-auto" /></TableCell>
                    <TableCell className="text-center"><XCircle className="h-5 w-5 text-red-500 mx-auto" /></TableCell>
                    <TableCell className="text-center"><XCircle className="h-5 w-5 text-red-500 mx-auto" /></TableCell>
                    <TableCell className="text-center"><XCircle className="h-5 w-5 text-red-500 mx-auto" /></TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">API Cost</TableCell>
                    <TableCell className="text-center">Very Low</TableCell>
                    <TableCell className="text-center">High</TableCell>
                    <TableCell className="text-center">Medium</TableCell>
                    <TableCell className="text-center">Medium</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Coding</TableCell>
                    <TableCell className="text-center">Excellent</TableCell>
                    <TableCell className="text-center">Excellent</TableCell>
                    <TableCell className="text-center">Excellent</TableCell>
                    <TableCell className="text-center">Very Good</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
            <p className="text-center text-sm text-muted-foreground mt-4">
              Detailed comparisons: <Link to="/deepseek-vs-chatgpt" className="text-primary hover:underline">vs ChatGPT</Link> | <Link to="/deepseek-vs-claude" className="text-primary hover:underline">vs Claude</Link> | <Link to="/deepseek-vs-gemini" className="text-primary hover:underline">vs Gemini</Link> | <Link to="/deepseek-vs-perplexity" className="text-primary hover:underline">vs Perplexity</Link>
            </p>
          </section>

          {/* Technology Section */}
          <div className="prose prose-lg mx-auto mb-16 max-w-4xl">
            <h2 className="text-3xl font-bold text-center mb-8">How DeepSeek Builds Its Models</h2>
            <p className="text-muted-foreground mb-6">
              DeepSeek AI leverages advanced neural architectures and innovative training methodologies to create powerful language models at a fraction of the cost of competitors. DeepSeek reported that the final training run of DeepSeek-V3 cost about US$6 million in GPU time, compared to the US$100+ million spent by competitors like OpenAI on models like GPT-4.
            </p>
            <p className="text-muted-foreground mb-6">
              Because the weights are open and the API is inexpensive, DeepSeek models are widely used for content generation, coding assistants and research. This guide is independent of DeepSeek; see the official site at deepseek.com for product details.
            </p>
          </div>

          {/* Products Grid */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-center mb-8">DeepSeek Products</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Link to="/deepseek-r1" className="p-6 rounded-xl bg-card shadow-lg border hover:shadow-xl transition-shadow">
                <Brain className="h-8 w-8 text-primary mb-3" />
                <h3 className="text-xl font-semibold mb-2">DeepSeek R1</h3>
                <p className="text-muted-foreground text-sm">Advanced reasoning model with chain-of-thought capabilities.</p>
              </Link>
              <Link to="/deepseek-coder" className="p-6 rounded-xl bg-card shadow-lg border hover:shadow-xl transition-shadow">
                <Code2 className="h-8 w-8 text-primary mb-3" />
                <h3 className="text-xl font-semibold mb-2">DeepSeek Coder</h3>
                <p className="text-muted-foreground text-sm">Specialized model for code generation and software development.</p>
              </Link>
              <Link to="/deepseek-api" className="p-6 rounded-xl bg-card shadow-lg border hover:shadow-xl transition-shadow">
                <Database className="h-8 w-8 text-primary mb-3" />
                <h3 className="text-xl font-semibold mb-2">DeepSeek API</h3>
                <p className="text-muted-foreground text-sm">Integrate DeepSeek into your applications with our powerful API.</p>
              </Link>
              <Link to="/deepseek-chat" className="p-6 rounded-xl bg-card shadow-lg border hover:shadow-xl transition-shadow">
                <Bot className="h-8 w-8 text-primary mb-3" />
                <h3 className="text-xl font-semibold mb-2">DeepSeek Chat</h3>
                <p className="text-muted-foreground text-sm">Conversational AI for natural dialogue and assistance.</p>
              </Link>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="mb-16 max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-8">Frequently Asked Questions</h2>
            <Accordion type="single" collapsible className="w-full">
              {faqItems.map((item, index) => (
                <AccordionItem key={index} value={`item-${index}`}>
                  <AccordionTrigger className="text-left">{item.question}</AccordionTrigger>
                  <AccordionContent>{item.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>

          {/* Related Links */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-center mb-8">Learn More</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Link to="/blog/what-is-deepseek-ai" className="p-6 rounded-xl bg-card shadow-lg border hover:shadow-xl transition-shadow">
                <h3 className="text-xl font-semibold mb-2">What is DeepSeek AI?</h3>
                <p className="text-muted-foreground">Complete guide to DeepSeek's technology and capabilities.</p>
              </Link>
              <Link to="/is-deepseek-safe" className="p-6 rounded-xl bg-card shadow-lg border hover:shadow-xl transition-shadow">
                <h3 className="text-xl font-semibold mb-2">Is DeepSeek Safe?</h3>
                <p className="text-muted-foreground">Security, privacy, and safety guide for DeepSeek AI.</p>
              </Link>
              <a href="https://github.com/deepseek-ai" target="_blank" rel="noopener noreferrer" className="p-6 rounded-xl bg-card shadow-lg border hover:shadow-xl transition-shadow">
                <h3 className="text-xl font-semibold mb-2">DeepSeek on GitHub</h3>
                <p className="text-muted-foreground">DeepSeek's official repositories: model code, papers and releases.</p>
              </a>
            </div>
          </section>

          {/* CTA Section */}
          <section className="text-center bg-primary/5 rounded-2xl p-12">
            <h2 className="text-3xl font-bold mb-4">Start Using DeepSeek AI Today</h2>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              Experience the power of open-source AI. Try DeepSeek for free via our Chrome extension or integrate via API.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a
                href="https://chromewebstore.google.com/detail/ai-sidebar-with-deepseek/inhcgfpbfdjbjogdfjbclgolkmhnooop"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-8 py-4 text-base font-medium rounded-full text-primary-foreground bg-primary hover:bg-primary/90 transition-all shadow-lg"
              >
                <Chrome className="mr-2 h-5 w-5" />
                Add to Chrome - It's Free
              </a>
              <Link
                to="/deepseek-api"
                className="inline-flex items-center justify-center px-6 py-3 border-2 border-primary text-base font-medium rounded-full text-primary bg-background hover:bg-accent transition-all"
              >
                Explore API
              </Link>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default DeepseekAI;
